import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  selectedFilters: ["age"],
  custom_segmentations: [],
  segmentationDataRedux: [],
  segmentationQuestions: {},
  segmentIds: {},
  selectItems: {},
  previousSegmentationData: [],
  qualifyingQuestionsData: [], // New state for qualifying questions
};

const mapApiOptionsToCustomFormat = (apiOptions) => {
  return apiOptions.map((option) => ({
    min_value: option.min_value || 0,
    max_value: option.max_value || 0,
    value: option.title,
    title: option.title,
    description: option.description,
    checked: true,
  }));
};

const transformApiToCustomSegmentationsQuestions = (apiData) => {
  return apiData.map((question) => ({
    question: question.question,
    type: question.type,
    is_required: question.is_required || true,
    is_qualifying_question: true,
    options:
      question.segmentation_question_option?.map((opt) => ({
        option_text: opt.option_text,
        is_correct: opt.is_correct || false,
        value: opt.value || "Placeholder",
      })) || [],
  }));
};

const transformApiToCustomSegmentations = (apiData) => {
  return apiData.map((segment) => ({
    id: segment.id,
    name: segment.name,
    type: segment.type,
    description: segment.description,
    category: segment.category,
    consumer_map_field: segment.consumer_map_field,
    is_additional_comment: false,
    additional_comment_placeholder: "",
    options:
      segment.segmentation_options?.length > 0
        ? mapApiOptionsToCustomFormat(segment.segmentation_options)
        : segment.options || [],
    questions:
      segment.segmentation_questions?.length > 0
        ? transformApiToCustomSegmentationsQuestions(
            segment.segmentation_questions
          )
        : [],
  }));
};

const getSelectItems = (segmentation_options) => {
  if (!Array.isArray(segmentation_options)) {
    // console.warn("segmentation_options is not an array:", segmentation_options);
    return [];
  }
  return segmentation_options.map((option) => ({
    value: option.title.toLowerCase().replace(/\s+/g, "-"),
    label: option.title,
    description: option.description,
  }));
};

export const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    // setSelectedFilters: (state, action) => {
    //   state.selectedFilters = action.payload;
    //   if (
    //     state.selectedFilters.includes("custom") &&
    //     !state.selectedFilters.includes("custom")
    //   ) {
    //     state.qualifyingQuestionsData = [];
    //   }
    //   const filteredSegments = state.segmentationDataRedux.filter((segment) =>
    //     action.payload.includes(segment.consumer_map_field)
    //   );
    //   const existingCustomSegments = state.custom_segmentations.filter(
    //     (seg) =>
    //       !state.segmentationDataRedux.some(
    //         (s) => s.consumer_map_field === seg.consumer_map_field
    //       )
    //   );
    //   state.custom_segmentations = [
    //     ...existingCustomSegments,
    //     ...transformApiToCustomSegmentations(filteredSegments),
    //   ];

    //   filteredSegments.forEach((segment) => {
    //     const key = segment.consumer_map_field;
    //     if (key !== "custom") {
    //       // Exclude "custom" from segmentIds
    //       state.segmentationQuestions[key] =
    //         segment.segmentation_questions || [];
    //       state.segmentIds[key] = [segment.id];
    //       state.selectItems[key] = getSelectItems(
    //         segment.segmentation_options || segment.options || []
    //       );
    //     }
    //   });
    // },
    setSelectedFilters: (state, action) => {
      const prevFilters = new Set(state.selectedFilters);
      state.selectedFilters = action.payload;

      if (prevFilters.has("custom") && !action.payload.includes("custom")) {
        state.qualifyingQuestionsData = [];
      }

      const newFilters = action.payload.filter(
        (filter) => !prevFilters.has(filter)
      );

      const newFilteredSegments = state.segmentationDataRedux.filter(
        (segment) => newFilters.includes(segment.consumer_map_field)
      );

      const existingSegments = state.custom_segmentations.filter((segment) =>
        action.payload.includes(segment.consumer_map_field)
      );

      const newTransformedSegments =
        transformApiToCustomSegmentations(newFilteredSegments);

      state.custom_segmentations = [
        ...existingSegments,
        ...newTransformedSegments.filter(
          (newSeg) =>
            !existingSegments.some(
              (existingSeg) =>
                existingSeg.consumer_map_field === newSeg.consumer_map_field
            )
        ),
      ];

      action.payload.forEach((filterKey) => {
        const segment = state.segmentationDataRedux.find(
          (s) => s.consumer_map_field === filterKey
        );
        if (segment && filterKey !== "custom") {
          state.segmentationQuestions[filterKey] =
            segment.segmentation_questions || [];
          state.segmentIds[filterKey] = [segment.id];
          state.selectItems[filterKey] = getSelectItems(
            segment.segmentation_options || segment.options || []
          );
        }
      });
    },
    setSegmentationDataRedux: (state, action) => {
      const payload = Array.isArray(action.payload) ? action.payload : [];
      state.segmentationDataRedux = payload;

      const filteredSegments = payload.filter((segment) =>
        state.selectedFilters.includes(segment.consumer_map_field)
      );
      const existingCustomSegments = state.custom_segmentations.filter(
        (seg) =>
          !payload.some((s) => s.consumer_map_field === seg.consumer_map_field)
      );
      state.custom_segmentations = [
        ...existingCustomSegments,
        ...transformApiToCustomSegmentations(filteredSegments),
      ];

      filteredSegments.forEach((segment) => {
        const key = segment.consumer_map_field;
        if (key !== "custom") {
          // Exclude "custom" from segmentIds
          state.segmentationQuestions[key] =
            segment.segmentation_questions || [];
          state.segmentIds[key] = [segment.id];
          state.selectItems[key] = getSelectItems(
            segment.segmentation_options || segment.options || []
          );
        }
      });

      if (!state.selectedFilters.length && payload.length > 0) {
        state.selectedFilters = ["age"];
      }
    },
    updateCustomSegmentations: (state, action) => {
      const payload = Array.isArray(action.payload) ? action.payload : [];
      state.custom_segmentations = payload;

      payload.forEach((segment) => {
        const key = segment.consumer_map_field;
        if (key !== "custom") {
          // Exclude "custom" from segmentIds
          state.selectItems[key] = getSelectItems(
            segment.segmentation_options || segment.options || []
          );
        }
      });
    },
    updateCheckbox: (state, action) => {
      const { filterKey, index, checked } = action.payload;
      const segment = state.custom_segmentations.find(
        (s) => s.consumer_map_field === filterKey
      );
      if (segment) {
        if (typeof checked === "boolean") {
          segment.options[index].checked = checked;
        } else {
          segment.options[index].checked = !segment.options[index].checked;
        }
      }
    },
    removeFilter: (state, action) => {
      const filterKey = action.payload;
      state.custom_segmentations = state.custom_segmentations.filter(
        (s) => s.consumer_map_field !== filterKey
      );
      state.selectedFilters = state.selectedFilters.filter(
        (f) => f !== filterKey
      );
      delete state.segmentationQuestions[filterKey];
      delete state.segmentIds[filterKey];
      delete state.selectItems[filterKey];
      if (filterKey === "custom") {
        state.qualifyingQuestionsData = state.qualifyingQuestionsData.filter(
          (q) => q.consumer_map_field !== filterKey
        );
      }
    },
    restoreFilter: (state, action) => {
      const filterKey = action.payload;
      if (
        !state.custom_segmentations.some(
          (s) => s.consumer_map_field === filterKey
        ) &&
        state.selectedFilters.length < 3
      ) {
        const segment = state.segmentationDataRedux.find(
          (s) => s.consumer_map_field === filterKey
        );
        if (segment) {
          const transformedSegment = transformApiToCustomSegmentations([
            segment,
          ])[0];
          state.custom_segmentations.push(transformedSegment);
          state.selectedFilters.push(filterKey);
          if (filterKey !== "custom") {
            // Exclude "custom" from segmentIds
            state.segmentationQuestions[filterKey] =
              segment.segmentation_questions || [];
            state.segmentIds[filterKey] = [segment.id];
            state.selectItems[filterKey] = getSelectItems(
              segment.segmentation_options || segment.options || []
            );
          }
        }
      }
    },
    clearAllFormDataSegmentation: (state) => {
      state.previousSegmentationData = [...state.segmentationDataRedux];
      state.selectedFilters = initialState.selectedFilters;
      state.custom_segmentations = initialState.custom_segmentations;
      state.segmentationDataRedux = initialState.segmentationDataRedux;
      state.segmentationQuestions = initialState.segmentationQuestions;
      state.segmentIds = initialState.segmentIds;
      state.selectItems = initialState.selectItems;
      state.qualifyingQuestionsData = initialState.qualifyingQuestionsData;
    },
    restorePreviousFilters: (state) => {
      if (state.previousSegmentationData.length > 0) {
        state.segmentationDataRedux = [...state.previousSegmentationData];
        const filteredSegments = state.previousSegmentationData.filter(
          (segment) =>
            state.selectedFilters.includes(segment.consumer_map_field)
        );
        const existingCustomSegments = state.custom_segmentations.filter(
          (seg) =>
            !state.segmentationDataRedux.some(
              (s) => s.consumer_map_field === seg.consumer_map_field
            )
        );
        state.custom_segmentations = [
          ...existingCustomSegments,
          ...transformApiToCustomSegmentations(filteredSegments),
        ];
        filteredSegments.forEach((segment) => {
          const key = segment.consumer_map_field;
          if (key !== "custom") {
            // Exclude "custom" from segmentIds
            state.segmentationQuestions[key] =
              segment.segmentation_questions || [];
            state.segmentIds[key] = [segment.id];
            state.selectItems[key] = getSelectItems(
              segment.segmentation_options || segment.options || []
            );
          }
        });
        if (
          !state.selectedFilters.length &&
          state.previousSegmentationData.length > 0
        ) {
          state.selectedFilters = ["age"];
        }
      }
    },
    setQualifyingQuestionsData: (state, action) => {
      state.qualifyingQuestionsData = action.payload;
    },
  },
});

export const {
  setSelectedFilters,
  setSegmentationDataRedux,
  updateCustomSegmentations,
  updateCheckbox,
  removeFilter,
  restoreFilter,
  clearAllFormDataSegmentation,
  restorePreviousFilters,
  setQualifyingQuestionsData,
} = filtersSlice.actions;

export default filtersSlice.reducer;
